import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface RegComplianceRow {
  id:          number;
  category:    string;
  month:       string;
  year:        string;
  subCategory: string;
  test:        string;
  method:      string;
  parameter:   string;
  projectId:   string;
  enteredDate: string | null;
  enteredBy:   string;
}

interface CategoryLookup    { id: number; name: string; }
interface SubCategoryLookup { id: number; categoryId: number; categoryName: string; name: string; }
interface MethodLookup      { id: string; code: string; name: string; }
interface ParameterLookup   { id: string; code: string; name: string; }
/** One row from ALPACA's TestMethod table — what BTLIMS calls a "Test". */
interface TestLookup        {
  id:           string;   // TestMethod.Oid
  testName:     string;   // 'Adjusted Gross Alpha'
  methodNumber: string;   // 'EPA 900.0'
  testCode:     string;   // 'TC0002'
  isGroup:      boolean;
}
interface TestParameterRow  { parameterId: string; parameterCode: string; parameterName: string; }

interface Lookups {
  categories:    CategoryLookup[];
  subCategories: SubCategoryLookup[];
  methods:       MethodLookup[];
  parameters:    ParameterLookup[];
  tests:         TestLookup[];
}

type Mode = 'view' | 'edit' | 'add';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
/** Date-only display via the shared helper (§14b.6 — MM/DD/YYYY, never inline). */
function fmtDate(v?: string | null): string {
  return fmtLocalDate(v) || '—';
}
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

/** Returns the month/year that follows the given one (December → January, +1 year). */
function nextMonthYear(month: string, year: string): { month: string; year: string } {
  const idx = MONTHS.indexOf(month);
  if (idx === -1) return { month, year };
  if (idx === 11) return { month: 'January', year: String(Number(year) + 1) };
  return { month: MONTHS[idx + 1]!, year };
}
function buildYearList(): string[] {
  const cur = new Date().getFullYear();
  const ys: string[] = [];
  for (let y = cur + 1; y >= cur - 5; y--) ys.push(String(y));
  return ys;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function RegulatoryCompliancePage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm: confirmDlg } = useConfirm();
  const today = new Date();
  const [month,        setMonth]        = useState<string>(MONTHS[today.getMonth()] ?? 'January');
  const [year,         setYear]         = useState<string>(String(today.getFullYear()));
  const [filterCat,    setFilterCat]    = useState<string>('');
  const [filterSub,    setFilterSub]    = useState<string>('');
  const [rows,         setRows]         = useState<RegComplianceRow[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [search,       setSearch]       = useState('');

  // Lookups
  const [lookups,      setLookups]      = useState<Lookups | null>(null);
  const [lookupErr,    setLookupErr]    = useState<string | null>(null);

  // Detail modal state
  const [detailOpen,   setDetailOpen]   = useState(false);
  const [detailMode,   setDetailMode]   = useState<Mode>('view');
  const [detailRow,    setDetailRow]    = useState<RegComplianceRow | null>(null);
  const [draft,        setDraft]        = useState<Partial<RegComplianceRow>>({});
  // AppDataGrid owns sort / per-column filter / pagination; this page keeps only
  // the selection set (driven by AppDataGrid's checkbox column) for Edit/Delete.
  const [selected,     setSelected]     = useState<Set<string | number>>(new Set());
  const [loadMs,       setLoadMs]       = useState<number | null>(null);
  /** True while a POST/PUT/DELETE is in flight. Disables Save/Delete buttons. */
  const [saving,       setSaving]       = useState(false);
  /** Last save/delete error message — shown inline at the bottom of the modal. */
  const [saveError,    setSaveError]    = useState<string | null>(null);
  /** Page-level success confirmation after Save / Update / Delete — routed
   *  through the shared notification service. */
  const showToast = useCallback((msg: string) => notify.success(msg), [notify]);

  // ── Save As (clone month) dialog state ────────────────────────────────────
  const [saveAsOpen,  setSaveAsOpen]  = useState(false);
  const [saveAsMonth, setSaveAsMonth] = useState<string>('');
  const [saveAsYear,  setSaveAsYear]  = useState<string>('');
  const [saveAsErr,   setSaveAsErr]   = useState<string | null>(null);
  const [saveAsBusy,  setSaveAsBusy]  = useState(false);
  /** Parameter checklist for the currently-selected (Test, Method) in the modal. */
  const [testParams,   setTestParams]   = useState<TestParameterRow[]>([]);
  const [paramsLoading, setParamsLoading] = useState(false);
  /** Which parameter ids are CURRENTLY ticked.  All-ticked = "AllParam". */
  const [checkedParams, setCheckedParams] = useState<Set<string>>(new Set());

  const yearList = useMemo(buildYearList, []);

  // ── Fetch list ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (month)     params.month       = month;
      if (year)      params.year        = year;
      if (filterCat) params.category    = filterCat;
      if (filterSub) params.subCategory = filterSub;
      const res = await api.get('/api/v1/regulatory-compliance', { params });
      setRows((res.data as { data: RegComplianceRow[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [month, year, filterCat, filterSub]);

  // ── Fetch lookups (once) ───────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/regulatory-compliance/lookups');
        if (!cancelled) setLookups((res.data as { data: Lookups }).data);
      } catch (e) {
        if (!cancelled) setLookupErr(e instanceof Error ? e.message : 'Failed to load lookups');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Initial load only.  All filter changes (Month / Year / Category / SubCategory)
  // now re-load ONLY when the user clicks "Retrieve" — so picking a new month
  // does not refetch the grid until the user is ready.
  useEffect(() => { void load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived data ───────────────────────────────────────────────────────────
  /** SubCategories that belong to the currently-selected toolbar Category. */
  const toolbarSubOptions = useMemo(() => {
    if (!lookups) return [];
    if (!filterCat) return lookups.subCategories;
    const cat = lookups.categories.find(c => c.name === filterCat);
    if (!cat) return [];
    return lookups.subCategories.filter(s => s.categoryId === cat.id);
  }, [lookups, filterCat]);

  /** Distinct test names ("Adjusted Gross Alpha", …) for the Test dropdown. */
  const distinctTestNames = useMemo(() => {
    if (!lookups) return [];
    return Array.from(new Set(lookups.tests.map(t => t.testName))).sort();
  }, [lookups]);

  /** Methods available for the currently-chosen Test. */
  const methodsForDraftTest = useMemo(() => {
    if (!lookups || !draft.test) return [];
    return lookups.tests
      .filter(t => t.testName === draft.test)
      .map(t => ({ id: t.id, methodNumber: t.methodNumber, testCode: t.testCode }))
      .filter(m => m.methodNumber);
  }, [lookups, draft.test]);

  /** TestMethod.Oid of the currently-selected (Test, Method) pair. */
  const draftTestMethodId = useMemo(() => {
    if (!lookups || !draft.test || !draft.method) return null;
    return lookups.tests.find(t => t.testName === draft.test && t.methodNumber === draft.method)?.id ?? null;
  }, [lookups, draft.test, draft.method]);

  /** Whenever the modal's chosen TestMethod changes, fetch its parameter
   *  checklist.  Default = ALL parameters ticked (= "AllParam" semantics). */
  useEffect(() => {
    if (!detailOpen || !draftTestMethodId) {
      setTestParams([]); setCheckedParams(new Set());
      return;
    }
    let cancelled = false;
    setParamsLoading(true);
    (async () => {
      try {
        const res = await api.get(`/api/v1/regulatory-compliance/tests/${draftTestMethodId}/parameters`);
        const list = (res.data as { data: TestParameterRow[] }).data ?? [];
        if (cancelled) return;
        setTestParams(list);
        // If the draft parameter field is "AllParam" or empty → tick everything.
        // If it's a "Customized" comma-list, parse and tick the matches.
        const wanted = (draft.parameter ?? '').trim();
        if (wanted && wanted !== 'AllParam' && wanted !== 'Customized') {
          const set = new Set(wanted.split(/[;,]/).map(s => s.trim()));
          setCheckedParams(new Set(list.filter(p => set.has(p.parameterName)).map(p => p.parameterId)));
        } else {
          setCheckedParams(new Set(list.map(p => p.parameterId)));   // default: all ticked
        }
      } finally {
        if (!cancelled) setParamsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailOpen, draftTestMethodId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Only the global search box filters here; AppDataGrid does per-column filter,
  // sort and pagination.
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(x =>
      x.category.toLowerCase().includes(t) ||
      x.subCategory.toLowerCase().includes(t) ||
      x.test.toLowerCase().includes(t) ||
      x.method.toLowerCase().includes(t) ||
      x.parameter.toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Columns for the shared grid (Parameter shows the blue pill; the rest are
  // plain black text via the default renderer).
  const gridCols: AppColumn<RegComplianceRow>[] = [
    { field: 'category',    header: 'Category',    width: 180 },
    { field: 'month',       header: 'Month',       width: 100 },
    { field: 'year',        header: 'Year',        width:  80 },
    { field: 'subCategory', header: 'SubCategory', width: 200 },
    { field: 'test',        header: 'Test',        width: 220 },
    { field: 'method',      header: 'Method',      width: 180 },
    { field: 'parameter',   header: 'Parameter',   width: 140,
      render: r => <span className="rc-pill rc-pill-blue">{r.parameter}</span>,
      text:   r => r.parameter },
  ];

  // ── Modal handlers ─────────────────────────────────────────────────────────
  function openDetail(row: RegComplianceRow) {
    setDetailRow(row);
    setDraft({ ...row });
    setDetailMode('view');
    setDetailOpen(true);
  }
  function openAddNew() {
    setDetailRow(null);
    setDraft({
      month, year,
      category: filterCat, subCategory: filterSub,
      test: '', method: '', parameter: 'AllParam',
    });
    setDetailMode('add');
    setDetailOpen(true);
  }
  function closeDetail() {
    setDetailOpen(false);
    setDetailRow(null);
    setDraft({});
    setSaveError(null);
    setSaving(false);
  }

  /** Build the POST/PUT body the backend expects (see RegulatoryComplianceSaveRequest). */
  function buildSavePayload(): {
    id:          number | null;
    category:    string;
    month:       string;
    year:        string;
    subCategory: string;
    test:        string;
    method:      string;
    parameter:   string;
    projectId:   string | null;
  } {
    return {
      id:          detailMode === 'edit' && detailRow ? detailRow.id : null,
      category:    draft.category    ?? '',
      month:       draft.month       ?? '',
      year:        draft.year        ?? '',
      subCategory: draft.subCategory ?? '',
      test:        draft.test        ?? '',
      method:      draft.method      ?? '',
      parameter:   draft.parameter   ?? 'AllParam',
      projectId:   draft.projectId   ?? null,
    };
  }

  /** Pull the human-friendly error string out of an axios failure. */
  function extractError(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
      const resp = (err as { response?: { data?: { error?: { message?: string } } } }).response;
      const msg  = resp?.data?.error?.message;
      if (msg) return msg;
    }
    return err instanceof Error ? err.message : 'Save failed';
  }

  async function handleSave() {
    setSaveError(null);

    // ── Client-side validation (mirrors the controller's Validate helper) ──
    if (!draft.category)    { setSaveError('Category is required');    return; }
    if (!draft.subCategory) { setSaveError('SubCategory is required'); return; }
    if (!draft.test)        { setSaveError('Test is required');        return; }
    // Method is optional — some tests (e.g., Coliform/Bacti) have no method.

    // Resolve the Parameter field based on the checkbox state:
    //   all ticked   → 'AllParam'
    //   none ticked  → '' (invalid)
    //   partial      → 'Customized'  (UX label; the actual list is also stored)
    let parameter = draft.parameter ?? 'AllParam';
    if (testParams.length > 0) {
      if (checkedParams.size === 0) {
        setSaveError('Pick at least one Parameter.');
        return;
      }
      parameter = checkedParams.size === testParams.length ? 'AllParam' : 'Customized';
    }

    const payload = { ...buildSavePayload(), parameter };
    const isAdd = detailMode === 'add';

    setSaving(true);
    try {
      // Shared ALPACA-style save flow: "Do you want to Save?" → save via the
      // existing API call → "Saved successfully." → close the modal + reload.
      await runSave({
        confirmMessage: isAdd ? 'Do you want to save this record?' : 'Do you want to update this record?',
        save: async () => {
          if (isAdd) await api.post('/api/v1/regulatory-compliance', payload);
          else if (detailRow) await api.put(`/api/v1/regulatory-compliance/${detailRow.id}`, payload);
        },
        successMessage: isAdd ? 'Saved successfully.' : 'Updated successfully.',
        onSaved: async () => { closeDetail(); await load(); },
        onError: (err) => setSaveError(extractError(err)),
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!detailRow) return;
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native confirm).
    if (!await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Do you want to delete this record?\n\n${detailRow.category} / ${detailRow.subCategory} / ${detailRow.test}`,
    })) return;

    setSaving(true);
    setSaveError(null);
    try {
      await api.delete(`/api/v1/regulatory-compliance/${detailRow.id}`);
      closeDetail();
      await load();
      showToast('Deleted successfully');
    } catch (err) {
      setSaveError(extractError(err));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Toolbar "Save As" — opens a small dialog where the user picks the TARGET
   * Month + Year.  The currently-displayed grid (i.e. the source Month/Year
   * + any Category/SubCategory filter) is what gets cloned.  Server-side
   * bulk INSERT…SELECT does the copy in one round-trip and rejects the
   * request with a "Please select different…" message when the target
   * period already has overlapping rows.
   */
  function openSaveAs() {
    if (rows.length === 0) { notify.warning('No rows to copy. Click Refresh first.'); return; }
    const def = nextMonthYear(month, year);
    setSaveAsMonth(def.month);
    setSaveAsYear(def.year);
    setSaveAsErr(null);
    setSaveAsOpen(true);
  }
  function closeSaveAs() {
    setSaveAsOpen(false);
    setSaveAsErr(null);
    setSaveAsBusy(false);
  }
  async function confirmSaveAs() {
    setSaveAsErr(null);
    if (!saveAsMonth || !saveAsYear) {
      setSaveAsErr('Pick a target Month and Year.');
      return;
    }
    if (saveAsMonth === month && saveAsYear === year) {
      setSaveAsErr('Target Month/Year must be different from the source.');
      return;
    }

    setSaveAsBusy(true);
    try {
      // Shared ALPACA-style save flow: "Do you want to Save?" → bulk clone →
      // "Saved successfully." → switch the view to the target period.
      await runSave({
        save: async () => {
          const res = await api.post('/api/v1/regulatory-compliance/save-as', {
            sourceMonth:       month,
            sourceYear:        year,
            sourceCategory:    filterCat || null,
            sourceSubCategory: filterSub || null,
            targetMonth:       saveAsMonth,
            targetYear:        saveAsYear,
          });
          const count = (res.data as { data?: { count?: number } }).data?.count ?? rows.length;
          // Switch view to the target so the new rows are visible immediately.
          setMonth(saveAsMonth);
          setYear(saveAsYear);
          setSaveAsOpen(false);
          // Re-load with the new period + same category/sub filter.
          const params: Record<string, string> = { month: saveAsMonth, year: saveAsYear };
          if (filterCat) params.category    = filterCat;
          if (filterSub) params.subCategory = filterSub;
          const listRes = await api.get('/api/v1/regulatory-compliance', { params });
          setRows((listRes.data as { data: RegComplianceRow[] }).data ?? []);
          // Informative count toast (the shared flow shows its own success box).
          notify.success(`Saved ${count} record${count === 1 ? '' : 's'} as ${saveAsMonth} ${saveAsYear}`);
        },
        successMessage: 'Saved As successfully.',
        // Duplicate-target error from the server is shown inline so the user
        // can pick a different Month/Year without dismissing the dialog.
        onError: (err) => setSaveAsErr(extractError(err)),
      });
    } finally {
      setSaveAsBusy(false);
    }
  }

  /** Edit the single selected row (toolbar button). */
  function openEditSelected() {
    if (selected.size === 0) { notify.warning('Select one row to edit.'); return; }
    if (selected.size > 1)   { notify.warning('Select only one row to edit.'); return; }
    const id  = Array.from(selected)[0]!;
    const row = rows.find(r => r.id === id);
    if (!row) return;
    setDetailRow(row); setDraft({ ...row }); setDetailMode('edit'); setDetailOpen(true);
  }
  /** Bulk-delete every selected row (toolbar button).
   *  Calls DELETE once per row in parallel; reports per-row failures. */
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.id));
    const preview = targets.slice(0, 5).map(t => `• ${t.category} / ${t.subCategory} / ${t.test}`).join('\n');
    const more    = targets.length > 5 ? `\n  …and ${targets.length - 5} more` : '';
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native confirm).
    if (!await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Do you want to delete ${targets.length} record${targets.length === 1 ? '' : 's'}?\n\n${preview}${more}`,
    })) return;

    const results = await Promise.allSettled(
      targets.map(t => api.delete(`/api/v1/regulatory-compliance/${t.id}`)),
    );
    const failed    = results.filter(r => r.status === 'rejected').length;
    const succeeded = targets.length - failed;
    if (failed > 0) {
      notify.error(`${succeeded} deleted, ${failed} failed. Check the server log for details.`);
    } else {
      showToast(`Deleted ${succeeded} successfully`);
    }
    setSelected(new Set());
    await load();
  }

  // Cascading Sub-category list inside the modal
  const draftSubOptions = useMemo(() => {
    if (!lookups) return [];
    if (!draft.category) return lookups.subCategories;
    const cat = lookups.categories.find(c => c.name === draft.category);
    if (!cat) return [];
    return lookups.subCategories.filter(s => s.categoryId === cat.id);
  }, [lookups, draft.category]);

  const editable = detailMode === 'add' || detailMode === 'edit';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .rc-filter-bar {
          display:flex; align-items:center; gap:10px; flex-wrap:wrap;
          padding:10px 14px; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line);
        }
        .rc-filter-bar label { font-size:12px; color:var(--ai-ink); font-weight:600; }
        .rc-sel {
          appearance:none; -webkit-appearance:none;
          background:#fff; color:var(--ai-ink); border:1px solid var(--ai-line); border-radius:4px;
          padding:5px 26px 5px 8px; font-size:12px; cursor:pointer; outline:none;
          min-width:140px;
          background-image: linear-gradient(45deg, transparent 50%, var(--ai-muted) 50%), linear-gradient(135deg, var(--ai-muted) 50%, transparent 50%);
          background-position: calc(100% - 12px) center, calc(100% - 7px) center;
          background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
        }
        .rc-sel:focus { border-color:var(--brand-accent, var(--ai-orange)); }
        .rc-sel:disabled { background:var(--ai-readonly-bg); color:var(--ai-muted); cursor:not-allowed; }
        .rc-sel-pill {
          display:inline-flex; align-items:center; gap:6px;
          background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-hover)); border:1px solid var(--ai-required-line);
          padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600;
        }
        .rc-sel-clear {
          background:transparent; border:none; color:var(--brand-accent-hover, var(--ai-orange-hover)); cursor:pointer;
          font-size:14px; line-height:1; padding:0 6px;
        }
        .rc-sel-clear:hover { color:var(--ai-orange-dark); }

        /* ── Parameter multi-select checklist (mirrors BTLIMS) ───────── */
        .rc-param-checklist {
          border: 1px solid var(--ai-line); border-radius: 4px; background: var(--ai-card);
          display: flex; flex-direction: column;
        }
        .rc-param-bulk {
          display: flex; gap: 6px; padding: 6px 8px;
          background: var(--ai-header-bg); border-bottom: 1px solid var(--ai-line);
        }
        .rc-param-pill {
          font-size: 10.5px; padding: 3px 9px; cursor: pointer;
          border: none; display: inline-flex; align-items: center; gap: 4px;
        }
        .rc-param-pill:hover { filter: brightness(0.95); }
        .rc-param-list {
          max-height: 220px; overflow-y: auto;
          display: grid; grid-template-columns: 1fr 1fr;
          padding: 4px 0;
        }
        .rc-param-row {
          display: flex; align-items: center; gap: 6px;
          padding: 4px 12px; cursor: pointer; font-size: 12px;
          color: var(--ai-ink); user-select: none;
        }
        /* Grey hover — never orange/blue rows (§18.6). */
        .rc-param-row:hover { background: var(--ai-row-hover); }
        .rc-param-row.on { color: var(--ai-green); font-weight: 600; }
        .rc-param-row input[type=checkbox] {
          width: 13px; height: 13px; cursor: pointer; accent-color: var(--brand-accent, var(--ai-orange));
          flex-shrink: 0;
        }

        /* ── Redesigned Detail / Edit / Add Modal ─────────────────────── */
        .rc-overlay {
          position:fixed; inset:0; background:rgba(0,0,0,.55);
          display:flex; align-items:center; justify-content:center;
          z-index:9000; backdrop-filter:blur(2px);
        }
        .rc-modal {
          width:min(94vw, 640px); max-height:92vh;
          background:var(--ai-card); border-radius:10px; overflow:hidden;
          display:flex; flex-direction:column;
          box-shadow:0 20px 60px rgba(0,0,0,.4);
        }
        .rc-modal-hdr {
          display:flex; align-items:center; justify-content:space-between;
          padding:14px 20px; background:var(--brand-accent, var(--ai-orange));
          color:#fff; flex-shrink:0;
        }
        .rc-modal-hdr-l { display:flex; align-items:center; gap:10px; }
        .rc-modal-icon {
          width:30px; height:30px; border-radius:6px;
          background:rgba(255,255,255,.12);
          display:flex; align-items:center; justify-content:center;
        }
        .rc-modal-icon i { font-size:16px; }
        .rc-modal-title-wrap { display:flex; flex-direction:column; gap:2px; }
        .rc-modal-hdr-title { font-size:14px; font-weight:600; line-height:1.2; }
        .rc-modal-hdr-sub { font-size:11px; opacity:.7; line-height:1.2; }
        .rc-modal-mode-badge {
          font-size:10px; padding:3px 10px; border-radius:10px;
          background:rgba(255,255,255,.18); text-transform:uppercase; letter-spacing:.06em;
          font-weight:700;
        }
        .rc-modal-close {
          width:30px; height:30px; border:none; border-radius:5px;
          background:rgba(255,255,255,.15); color:#fff; cursor:pointer;
          display:flex; align-items:center; justify-content:center; font-size:18px;
        }
        .rc-modal-close:hover { background:rgba(255,255,255,.3); }

        .rc-modal-body {
          padding:6px 24px 22px; overflow-y:auto; flex:1;
          display:flex; flex-direction:column; gap:4px;
        }
        .rc-section { margin-top:16px; }
        .rc-section:first-child { margin-top:8px; }
        .rc-section-hdr {
          font-size:10.5px; color:var(--ai-muted); font-weight:700;
          text-transform:uppercase; letter-spacing:.08em;
          padding:6px 0 8px;
          border-bottom:1px solid var(--ai-line);
          margin-bottom:12px;
          display:flex; align-items:center; gap:6px;
        }
        .rc-section-hdr i { font-size:13px; color:var(--ai-muted); }
        .rc-grid2 {
          display:grid; grid-template-columns:1fr 1fr; gap:12px 18px;
        }
        .rc-field { display:flex; flex-direction:column; gap:5px; }
        .rc-field.rc-field-full { grid-column: span 2; }
        .rc-field-required::after { content:" *"; color:var(--brand-accent, var(--ai-orange)); font-weight:700; }
        .rc-field label {
          font-size:11px; color:var(--ai-ink); font-weight:600;
          letter-spacing:.02em;
        }
        .rc-field input, .rc-field select {
          font-size:13px; padding:7px 9px;
          border:1px solid var(--ai-line); border-radius:4px;
          background:#fff; color:var(--ai-ink); outline:none;
          transition: border-color .12s, background-color .12s;
        }
        .rc-field input:focus, .rc-field select:focus {
          border-color: var(--brand-accent, var(--ai-orange));
          box-shadow:0 0 0 2px color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 14%, transparent);
        }
        .rc-field input:disabled, .rc-field select:disabled {
          background:#f5f5f5; /* token-exempt: readonly fill */ color:var(--ai-muted); cursor:not-allowed;
        }
        .rc-field-val {
          font-size:13px; padding:7px 0 6px; color:var(--ai-ink);
          border-bottom:1px solid var(--ai-line); min-height:18px;
        }
        .rc-field-val strong { color:var(--ai-ink); }
        .rc-hint {
          font-size:11px; color:var(--ai-muted); margin-top:-2px;
        }
        .rc-pill {
          display:inline-flex; align-items:center; gap:4px;
          padding:1px 8px; border-radius:10px;
          font-size:10.5px; font-weight:600;
        }
        .rc-pill-blue { background:var(--ai-pill-info-bg); color:var(--ai-blue); }
        .rc-pill-orange { background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-hover)); }

        .rc-modal-ftr {
          display:flex; align-items:center; justify-content:space-between;
          gap:8px; padding:12px 20px; background:var(--ai-header-bg);
          border-top:1px solid var(--ai-line); flex-shrink:0;
        }
        .rc-modal-ftr-l { display:flex; gap:8px; }
        .rc-modal-ftr-r { display:flex; gap:8px; align-items:center; }
        .rc-msg { font-size:12px; color:var(--ai-green); font-weight:600; }
        .rc-btn {
          padding:7px 16px; font-size:12px; font-weight:600;
          border-radius:5px; cursor:pointer; border:1px solid transparent;
          display:inline-flex; align-items:center; gap:6px;
          transition: background-color .12s, border-color .12s;
        }
        .rc-btn-primary  { background:var(--brand-accent, var(--ai-orange)); color:#fff; }
        .rc-btn-primary:hover  { background:var(--brand-accent-hover, var(--ai-orange-hover)); }
        .rc-btn-secondary{ background:#fff; color:var(--ai-ink); border-color:var(--ai-line); }
        .rc-btn-secondary:hover{ background:var(--ai-row-hover); border-color:var(--ai-line); }
        .rc-btn-danger   { background:#fff; color:var(--ai-red); border-color:var(--ai-pill-alert-fg); }
        .rc-btn-danger:hover   { background:var(--ai-pill-alert-bg); border-color:var(--ai-red); }
        .rc-btn-danger:disabled { opacity:.4; cursor:not-allowed; }
      `}</style>

      <div className="bl-page">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span>Regulatory Compliance</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Category and Test Setup</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page,
                with the selection pill as a title-row chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="regcompliance" loadMs={loadMs} loading={loading} />
              {selected.size > 0 && (
                <span className="rc-sel-pill" style={{ marginLeft: 10 }}>
                  <i className="ti ti-circle-check" /> {selected.size} selected
                  <button className="rc-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                </span>
              )}
            </div>
            <div className="dp-subtitle">
              Source: BTLIMS · {loading ? '…' : filtered.length} items · {month} {year}
              {lookupErr && <span style={{ color:'var(--ai-red)', marginLeft:10 }}>· Lookups failed: {lookupErr}</span>}
            </div>
          </div>

          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total ({month} {year})</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : filtered.length}</div>
              <div className="kpi-chg">items in view</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Categories</div>
              <div className="kpi-val kpi-teal">{lookups?.categories.length ?? '…'}</div>
              <div className="kpi-chg">in BTLIMS lookup</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Sub Categories</div>
              <div className="kpi-val">{lookups?.subCategories.length ?? '…'}</div>
              <div className="kpi-chg">linked to category</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Tests / Methods</div>
              <div className="kpi-val" style={{ fontSize: 18 }}>
                {lookups?.tests.length ?? '…'}<span style={{ color:'var(--ai-muted)' }}> / </span>{lookups?.methods.length ?? '…'}
              </div>
              <div className="kpi-chg">from ALPACA test setup</div>
            </div>
          </div>
        </div>

        {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard
            v2); only the grid card below is white. ──────────────────────── */}
        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row — form-specific "Save As" in the
              left slot, then canonical New · Edit · Delete · Refresh · Export to. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              left={
                <button
                  className="ca-btn"
                  onClick={openSaveAs}
                  disabled={saving || rows.length === 0}
                  title={rows.length === 0
                    ? 'Refresh some rows first'
                    : `Clone the current ${rows.length} filtered row${rows.length === 1 ? '' : 's'} into a different Month/Year`}
                >
                  <i className="ti ti-copy" /> Save As
                </button>
              }
              newLabel="Add New"
              onNew={openAddNew}
              newDisabled={!lookups}
              onEdit={openEditSelected}
              editDisabled={selected.size !== 1}
              onDelete={deleteSelected}
              deleteDisabled={selected.size === 0}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: `regulatory-compliance-${month}-${year}`, ...gridExportMatrix(filtered, gridCols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                type="text"
                placeholder="Search category, sub-category, test, method, parameter…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
            </div>
            {(filtered.length !== rows.length) && (
              <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>
                {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>

          {/* ONE white grid card — filter bar strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

            {/* Filter bar (Month/Year/Category/SubCategory + Retrieve) */}
            <div className="rc-filter-bar">
              <label>Month</label>
              <select className="rc-sel" value={month} onChange={e => setMonth(e.target.value)}>
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>

              <label>Year</label>
              <select className="rc-sel" value={year} onChange={e => setYear(e.target.value)}>
                {yearList.map(y => <option key={y} value={y}>{y}</option>)}
              </select>

              <label>Category</label>
              <select className="rc-sel" value={filterCat} onChange={e => { setFilterCat(e.target.value); setFilterSub(''); }}>
                <option value="">(All)</option>
                {lookups?.categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>

              <label>SubCategory</label>
              <select
                className="rc-sel"
                value={filterSub}
                onChange={e => setFilterSub(e.target.value)}
                disabled={!filterCat || toolbarSubOptions.length === 0}
                title={!filterCat ? 'Pick a Category first' : ''}
              >
                <option value="">(All)</option>
                {toolbarSubOptions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>

              <button className="ca-btn" onClick={load} disabled={loading}>
                {loading ? <><LoaderSymbol size={14} /> Loading…</> : <><span className="tb-i">🔄</span> Refresh</>}
              </button>
            </div>

            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, var(--ai-orange))', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and the selection checkboxes are all
                 standardised in <AppDataGrid>. */
              <AppDataGrid<RegComplianceRow>
                rows={filtered}
                columns={gridCols}
                getRowId={r => r.id}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowClick={openDetail}
                emptyText={`No records found for ${month} ${year}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* ─── Redesigned Detail / Edit / Add Modal ─────────────────────── */}
      {detailOpen && (
        <div className="rc-overlay" onClick={e => { if (e.target === e.currentTarget) closeDetail(); }}>
          <div className="rc-modal">
            <div className="rc-modal-hdr">
              <div className="rc-modal-hdr-l">
                <div className="rc-modal-icon">
                  <i className={detailMode === 'add' ? 'ti ti-plus' : detailMode === 'edit' ? 'ti ti-edit' : 'ti ti-eye'} />
                </div>
                <div className="rc-modal-title-wrap">
                  <span className="rc-modal-hdr-title">
                    {detailMode === 'add' ? 'New DWRC Category and Test'
                     : detailMode === 'edit' ? 'Edit DWRC Category and Test'
                     : 'DWRC Category and Test'}
                  </span>
                  <span className="rc-modal-hdr-sub">
                    {detailMode === 'add' ? 'Pick a category, sub-category, and the test/method combination from ALPACA.'
                     : detailRow ? `Record #${detailRow.id} · entered ${fmtDate(detailRow.enteredDate)}` : ''}
                  </span>
                </div>
                <span className="rc-modal-mode-badge">{detailMode}</span>
              </div>
              <button className="rc-modal-close" onClick={closeDetail}>×</button>
            </div>

            <div className="rc-modal-body">
              {/* ── Section 1: Classification ── */}
              <div className="rc-section">
                <div className="rc-section-hdr">
                  <i className="ti ti-category" /> Classification
                </div>
                <div className="rc-grid2">
                  <div className="rc-field">
                    <label className={editable ? 'rc-field-required' : ''}>Category</label>
                    {editable ? (
                      <select
                        value={draft.category ?? ''}
                        onChange={e => setDraft(d => ({ ...d, category: e.target.value, subCategory: '' }))}
                      >
                        <option value="">— select —</option>
                        {lookups?.categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    ) : (
                      <div className="rc-field-val"><strong>{detailRow?.category || '—'}</strong></div>
                    )}
                  </div>

                  <div className="rc-field">
                    <label className={editable ? 'rc-field-required' : ''}>SubCategory</label>
                    {editable ? (
                      <select
                        value={draft.subCategory ?? ''}
                        onChange={e => setDraft(d => ({ ...d, subCategory: e.target.value }))}
                        disabled={!draft.category}
                      >
                        <option value="">{draft.category ? '— select —' : '(pick a Category first)'}</option>
                        {draftSubOptions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    ) : (
                      <div className="rc-field-val">{detailRow?.subCategory || '—'}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Section 2: Reporting Period ── */}
              <div className="rc-section">
                <div className="rc-section-hdr">
                  <i className="ti ti-calendar" /> Reporting Period
                </div>
                <div className="rc-grid2">
                  <div className="rc-field">
                    <label>Month</label>
                    {editable ? (
                      <select value={draft.month ?? ''} onChange={e => setDraft(d => ({ ...d, month: e.target.value }))}>
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (
                      <div className="rc-field-val">{detailRow?.month || '—'}</div>
                    )}
                  </div>
                  <div className="rc-field">
                    <label>Year</label>
                    {editable ? (
                      <select value={draft.year ?? ''} onChange={e => setDraft(d => ({ ...d, year: e.target.value }))}>
                        {yearList.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    ) : (
                      <div className="rc-field-val">{detailRow?.year || '—'}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Section 3: Test / Method / Parameter ── */}
              <div className="rc-section">
                <div className="rc-section-hdr">
                  <i className="ti ti-test-pipe" /> Test, Method &amp; Parameter
                </div>
                <div className="rc-grid2">
                  {/* Test Name — dropdown of distinct TestName values from TestMethod */}
                  <div className="rc-field">
                    <label className={editable ? 'rc-field-required' : ''}>Test Name</label>
                    {editable ? (
                      <select
                        value={draft.test ?? ''}
                        onChange={e => setDraft(d => ({ ...d, test: e.target.value, method: '' }))}
                      >
                        <option value="">— select —</option>
                        {distinctTestNames.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    ) : (
                      <div className="rc-field-val"><strong>{detailRow?.test || '—'}</strong></div>
                    )}
                  </div>

                  {/* Method — cascades from picked Test, shows only its valid methods.
                      Optional: some tests (e.g. Coliform/Bacti) have no method. */}
                  <div className="rc-field">
                    <label>Method</label>
                    {editable ? (
                      <select
                        value={draft.method ?? ''}
                        onChange={e => setDraft(d => ({ ...d, method: e.target.value }))}
                        disabled={!draft.test}
                      >
                        <option value="">{draft.test ? '— select —' : '(pick a Test first)'}</option>
                        {methodsForDraftTest.map(m => (
                          <option key={m.id} value={m.methodNumber}>{m.methodNumber}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="rc-field-val">{detailRow?.method || '—'}</div>
                    )}
                  </div>

                  {/* Parameter — multi-select checkbox list (loaded after Test+Method) */}
                  <div className="rc-field rc-field-full">
                    <label>
                      Parameters
                      {editable && testParams.length > 0 && (
                        <span className="rc-pill rc-pill-blue" style={{ marginLeft: 8, textTransform: 'none' }}>
                          {checkedParams.size === testParams.length
                            ? `AllParam (${testParams.length})`
                            : checkedParams.size === 0
                              ? '(none selected)'
                              : `Customized (${checkedParams.size} / ${testParams.length})`}
                        </span>
                      )}
                    </label>
                    {editable ? (
                      paramsLoading ? (
                        <div className="rc-field-val" style={{ color: 'var(--ai-muted)' }}>
                          <LoaderSymbol size={14} /> Loading parameters…
                        </div>
                      ) : !draftTestMethodId ? (
                        <div className="rc-field-val" style={{ color: 'var(--ai-muted)', fontStyle: 'italic' }}>
                          (pick a Test and Method first)
                        </div>
                      ) : testParams.length === 0 ? (
                        <div className="rc-field-val" style={{ color: 'var(--ai-amber)' }}>
                          <i className="ti ti-alert-triangle" /> No parameters defined for this Test/Method in ALPACA Test Setup.
                        </div>
                      ) : (
                        <div className="rc-param-checklist">
                          {/* Bulk toggles */}
                          <div className="rc-param-bulk">
                            <button type="button" className="rc-pill rc-pill-blue rc-param-pill"
                              onClick={() => setCheckedParams(new Set(testParams.map(p => p.parameterId)))}>
                              <i className="ti ti-checks" /> Select all
                            </button>
                            <button type="button" className="rc-pill rc-pill-blue rc-param-pill"
                              style={{ background: 'var(--ai-pill-alert-bg)', color: 'var(--ai-pill-alert-fg)' }}
                              onClick={() => setCheckedParams(new Set())}>
                              <i className="ti ti-square" /> Clear all
                            </button>
                          </div>
                          <div className="rc-param-list">
                            {testParams.map(p => {
                              const ticked = checkedParams.has(p.parameterId);
                              return (
                                <label key={p.parameterId} className={`rc-param-row${ticked ? ' on' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={ticked}
                                    onChange={e => {
                                      setCheckedParams(prev => {
                                        const n = new Set(prev);
                                        if (e.target.checked) n.add(p.parameterId); else n.delete(p.parameterId);
                                        return n;
                                      });
                                    }}
                                  />
                                  <span>{p.parameterName}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="rc-field-val">{detailRow?.parameter || '—'}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Section 4: Audit (view/edit only) ── */}
              {detailMode !== 'add' && detailRow && (
                <div className="rc-section">
                  <div className="rc-section-hdr">
                    <i className="ti ti-history" /> Audit
                  </div>
                  <div className="rc-grid2">
                    <div className="rc-field">
                      <label>Entered Date</label>
                      <div className="rc-field-val">{fmtDate(detailRow.enteredDate)}</div>
                    </div>
                    <div className="rc-field">
                      <label>Entered By</label>
                      <div className="rc-field-val">{detailRow.enteredBy || '—'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rc-modal-ftr">
              <div className="rc-modal-ftr-l">
                {detailMode === 'view' && detailRow && (
                  <>
                    <button className="rc-btn rc-btn-primary" onClick={() => setDetailMode('edit')} disabled={saving}>
                      <span className="tb-i">✎</span> Edit
                    </button>
                    <button className="rc-btn rc-btn-danger" onClick={handleDelete} disabled={saving}>
                      {saving ? <BusyIcon busy icon="ti-trash" /> : <span className="tb-i">🗑</span>} {saving ? 'Deleting…' : 'Delete'}
                    </button>
                  </>
                )}
                {(detailMode === 'edit' || detailMode === 'add') && (
                  <button className="rc-btn rc-btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>}
                    {saving
                      ? (detailMode === 'add' ? 'Saving…' : 'Updating…')
                      : (detailMode === 'add' ? 'Save' : 'Update')}
                  </button>
                )}
                {detailMode === 'edit' && (
                  <button className="rc-btn rc-btn-secondary" onClick={() => { setDraft({ ...detailRow! }); setDetailMode('view'); setSaveError(null); }} disabled={saving}>
                    <span className="tb-i">↩︎</span> Cancel Edit
                  </button>
                )}
                {saveError && (
                  <span className="rc-msg" style={{ color: 'var(--ai-red)', marginLeft: 8 }}>
                    <i className="ti ti-alert-circle" /> {saveError}
                  </span>
                )}
              </div>
              <div className="rc-modal-ftr-r">
                <button className="rc-btn rc-btn-secondary" onClick={closeDetail} disabled={saving}>
                  <span className="tb-i">↩︎</span> Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Save As (clone month) dialog ──────────────────────────────── */}
      {saveAsOpen && (
        <div className="rc-overlay" onClick={e => { if (e.target === e.currentTarget && !saveAsBusy) closeSaveAs(); }}>
          <div className="rc-modal" style={{ width: 'min(94vw, 520px)' }}>
            <div className="rc-modal-hdr">
              <div className="rc-modal-hdr-l">
                <div className="rc-modal-icon"><i className="ti ti-copy" /></div>
                <div className="rc-modal-title-wrap">
                  <span className="rc-modal-hdr-title">Save As — Clone to a new period</span>
                  <span className="rc-modal-hdr-sub">
                    Source: {month} {year}
                    {filterCat ? ` · ${filterCat}` : ''}{filterSub ? ` · ${filterSub}` : ''}
                    {` · ${rows.length} row${rows.length === 1 ? '' : 's'}`}
                  </span>
                </div>
              </div>
              <button className="rc-modal-close" onClick={closeSaveAs} disabled={saveAsBusy}>×</button>
            </div>

            <div className="rc-modal-body">
              <div className="rc-section">
                <div className="rc-section-hdr"><i className="ti ti-calendar" /> Target Period</div>
                <div className="rc-grid2">
                  <div className="rc-field">
                    <label className="rc-field-required">Month</label>
                    <select value={saveAsMonth} onChange={e => { setSaveAsMonth(e.target.value); setSaveAsErr(null); }} disabled={saveAsBusy}>
                      <option value="">— select —</option>
                      {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="rc-field">
                    <label className="rc-field-required">Year</label>
                    <select value={saveAsYear} onChange={e => { setSaveAsYear(e.target.value); setSaveAsErr(null); }} disabled={saveAsBusy}>
                      <option value="">— select —</option>
                      {yearList.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div className="rc-hint" style={{ marginTop: 10 }}>
                  All {rows.length} currently-displayed row{rows.length === 1 ? '' : 's'} will be copied into the selected Month / Year with the same Category, SubCategory, Test, Method, and Parameter values.
                </div>
              </div>
            </div>

            <div className="rc-modal-ftr">
              <div className="rc-modal-ftr-l">
                <button className="rc-btn rc-btn-primary" onClick={confirmSaveAs} disabled={saveAsBusy}>
                  {saveAsBusy ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>}
                  {saveAsBusy ? 'Saving…' : 'Save As'}
                </button>
                {saveAsErr && (
                  <span className="rc-msg" style={{ color: 'var(--ai-red)', marginLeft: 8, maxWidth: 340 }}>
                    <i className="ti ti-alert-circle" /> {saveAsErr}
                  </span>
                )}
              </div>
              <div className="rc-modal-ftr-r">
                <button className="rc-btn rc-btn-secondary" onClick={closeSaveAs} disabled={saveAsBusy}>
                  <span className="tb-i">↩︎</span> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
